import React from 'react';
import { motion } from 'framer-motion';
import { Check, Clock, Calendar, TrendingUp, Settings, Info } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { HabitGoal } from '../../store/habitStore';

interface HabitCardProps {
  habit: HabitGoal;
  onComplete: (id: string) => void;
  onEdit: (id: string) => void;
}

export function HabitCard({ habit, onComplete, onEdit }: HabitCardProps) {
  const progress = (habit.completedDays / habit.targetDays) * 100;
  const today = format(new Date(), 'yyyy-MM-dd');
  const isCompletedToday = habit.lastCompleted === today;

  const getTimeStatus = () => {
    if (!habit.reminderTime) return null;
    const now = new Date();
    const [hours, minutes] = habit.reminderTime.split(':').map(Number);
    const reminderTime = new Date();
    reminderTime.setHours(hours, minutes);

    const diff = reminderTime.getTime() - now.getTime();
    const minutesUntil = Math.floor(diff / (1000 * 60));

    if (minutesUntil > 0 && minutesUntil <= 60) {
      return `Due in ${minutesUntil} minutes`;
    } else if (minutesUntil <= 0 && minutesUntil > -60) {
      return 'Due now';
    }
    return format(reminderTime, 'h:mm a');
  };

  const timeStatus = getTimeStatus();

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="theme-card p-4 relative overflow-hidden group"
    >
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />
      
      {/* Content */}
      <div className="relative space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => onComplete(habit.id)}
              disabled={isCompletedToday}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                isCompletedToday
                  ? 'bg-success text-success-foreground'
                  : 'bg-primary/10 hover:bg-primary/20'
              }`}
            >
              <Check className="w-4 h-4" />
            </button>
            <div>
              <div className="font-medium">{habit.name}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <Calendar className="w-3 h-3" />
                <span>{habit.completedDays}/{habit.targetDays} this week</span>
                {timeStatus && (
                  <>
                    <span className="text-muted-foreground">•</span>
                    <Clock className="w-3 h-3" />
                    <span>{timeStatus}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {habit.currentStreak > 0 && (
              <div className="flex items-center gap-1 text-success text-sm bg-success/10 px-2 py-1 rounded-full">
                <TrendingUp className="w-3 h-3" />
                <span>{habit.currentStreak}d</span>
              </div>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Weekly Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-primary"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="flex items-center gap-1">
            <TrendingUp className="w-3 h-3 text-primary" />
            <span>Best: {habit.bestStreak}d</span>
          </div>
          {habit.lastCompleted && (
            <div className="flex items-center gap-1">
              <Check className="w-3 h-3 text-success" />
              <span>Last: {formatDistanceToNow(new Date(habit.lastCompleted))} ago</span>
            </div>
          )}
          {habit.suggestedTarget && habit.suggestedTarget !== habit.targetDays && (
            <div className="flex items-center gap-1 text-warning">
              <Info className="w-3 h-3" />
              <span>Suggested: {habit.suggestedTarget}/week</span>
            </div>
          )}
        </div>

        {/* Edit Button - Shows on Hover */}
        <motion.button
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 0 }}
          whileHover={{ opacity: 1, x: 0 }}
          onClick={() => onEdit(habit.id)}
          className="absolute top-4 right-12 text-xs text-primary bg-background/80 backdrop-blur-sm px-2 py-1 rounded-lg flex items-center gap-1"
        >
          <Settings className="w-3 h-3" />
          Edit
        </motion.button>
      </div>
    </motion.div>
  );
}